import { useMemo, useRef } from "react";
import { clampRect, scaleRect, transformStrokes } from "../../masterImages/geometry";
import { computeMask } from "../../masterImages/masking";
import type { MasterImage, Pixels } from "../../masterImages/types";

export interface Outline {
  canvas: HTMLCanvasElement;
  x: number;
  y: number;
}

export const CUT_AWAY = "rgba(0, 0, 0, 0.6)";
export const CONTOUR = "#ffffff";

const KEPT = 128;
const CUT_AWAY_RGBA = [0, 0, 0, 153];
const CONTOUR_RGBA = [255, 255, 255, 255];

const resultOverlay = (alpha: Uint8Array, width: number, height: number) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const image = new ImageData(width, height);
  const kept = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < width && y < height && alpha[y * width + x] >= KEPT;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (!kept(x, y)) {
        image.data.set(CUT_AWAY_RGBA, i * 4);
      } else if (
        (x > 0 && !kept(x - 1, y)) ||
        (x < width - 1 && !kept(x + 1, y)) ||
        (y > 0 && !kept(x, y - 1)) ||
        (y < height - 1 && !kept(x, y + 1))
      ) {
        image.data.set(CONTOUR_RGBA, i * 4);
      }
    }
  }
  canvas.getContext("2d")!.putImageData(image, 0, 0);
  return canvas;
};

export const useOutlines = (pixels: Pixels | null, scale: number, masterImage: MasterImage) => {
  const cache = useRef<{ pixels: Pixels | null; entries: Map<string, { key: string; outline: Outline }> }>({
    pixels: null,
    entries: new Map(),
  });

  return useMemo(() => {
    const outlines = new Map<string, Outline>();
    if (!pixels) return outlines;
    const previous = cache.current.pixels === pixels ? cache.current.entries : new Map();
    const entries = new Map<string, { key: string; outline: Outline }>();
    for (const region of masterImage.regions) {
      const rect = clampRect(scaleRect(region, scale), pixels);
      if (rect.width < 2 || rect.height < 2) continue;
      const tolerance = region.tolerance ?? masterImage.tolerance;
      const key = JSON.stringify([rect, tolerance, region.strokes]);
      let entry = previous.get(region.id);
      if (!entry || entry.key !== key) {
        const alpha = computeMask({
          image: pixels,
          rect,
          tolerance,
          strokes: transformStrokes(region.strokes, scale, 0, 0),
        });
        entry = { key, outline: { canvas: resultOverlay(alpha, rect.width, rect.height), x: rect.x, y: rect.y } };
      }
      entries.set(region.id, entry);
      outlines.set(region.id, entry.outline);
    }
    cache.current = { pixels, entries };
    return outlines;
  }, [pixels, scale, masterImage.regions, masterImage.tolerance]);
};
